// Hành Trang (ITEMS §8–§9, UI_SPEC §18.1): trang bị đang mặc, túi, vật phẩm xếp chồng, bộ lọc tự
// phân giải, Rương Chưa Mở (Tiên+ chờ Khai Bảo). Nối sim thuần (src/sim/items) với UI.
import { signal, computed, batch } from '@preact/signals';
import { BALANCE } from '@data/balance';
import { EQUIP_SLOTS, type EquipSlot, type ItemInstance, type Rarity } from '@data/items/types';
import { FIRST_REALM, LAST_REALM, subStageCount } from '@data/powerScale';
import { isBaseId, baseDef } from '@data/items/bases';
import { isGlyphId } from '@data/items/glyphs';
import { isUniqueId } from '@data/items/uniques';
import { maybeAffix } from '@data/items/affixes';
import { isStackKey, parseStackKey, goodsDef } from '@data/items/goods';
import { createRng, deriveSeed } from '@sim/core/rng';
import { emptyEquipped, receiveItems, type Equipped, type ReceiveReport } from '@sim/items/bag';
import { combatStats, compareToEquipped, loadoutPower, slotsForKind, type Loadout } from '@sim/items/stats';
import {
  applyTransfer,
  defaultFilter,
  enhanceCost,
  enhanceOnce,
  isProtected,
  isRarity,
  matchesFilter,
  salvageYield,
  transferQuote,
  type EnhanceTransfer,
  type SalvageFilter,
} from '@sim/items/nurture';
import { rarityIndex } from '@sim/items/generate';
import { gearCultBonus, playerBase, playerGiai, trialCtxFor } from '@sim/world/ctx';
import { pillBlock, usePill as simUsePill } from '@sim/crafting';
import { isPlainObject } from '@save/types';
import { registerSlice } from '../registry';
import { now } from '../clock';
import { flush, markDirty, saveSeed } from '../persistence';
import { logLine } from './log';
import { canAfford, creditWallet, spend } from './wallet';
import { applyPillUse, cultivation, setGearBonus, setIdleHooks, settleTo } from './cultivation';

export interface InventoryState {
  equipped: Equipped;
  bag: ItemInstance[];
  goods: Record<string, number>;
  filter: SalvageFilter;
  /** Tiên+ nhận khi vắng: chờ người chơi tự mở (Khai Bảo idle, ITEMS §9). */
  unopened: ItemInstance[];
  /** Món mới vào túi chưa xem (chấm `new` ở ô). */
  fresh: string[];
}

export function defaultInventory(): InventoryState {
  return { equipped: emptyEquipped(), bag: [], goods: {}, filter: defaultFilter(), unopened: [], fresh: [] };
}

const _inv = signal<InventoryState>(defaultInventory());
export const inventory = computed(() => _inv.value);

/** Lực Chiến hiện tại (số chính của Hành Trang). */
export const combatPowerNow = computed(() => {
  const c = cultivation.value;
  return loadoutPower(playerBase(c), _inv.value.equipped as Loadout);
});

/** Ô mặc có món tốt hơn trong túi (dấu `task`): ΔLC ≥ 2%. */
export const betterInBag = computed(() => {
  const c = cultivation.value;
  const inv = _inv.value;
  const base = playerBase(c);
  const out = new Set<EquipSlot>();
  const byUid = new Map<string, number>();
  for (const it of inv.bag) {
    const cmp = compareToEquipped(base, inv.equipped as Loadout, it);
    byUid.set(it.uid, cmp.ratio);
    if (cmp.ratio >= BALANCE.UPGRADE_MIN_LC_GAIN) out.add(cmp.slot);
  }
  return { slots: out, ratio: byUid };
});

// ── Phản hồi nhặt đồ cho vỏ (cột sáng, chuông, bay vào túi). UI đọc rồi xóa.
export interface LootFx {
  seq: number;
  rarity: ItemInstance['rarity'];
  uid: string;
}
const _fx = signal<LootFx[]>([]);
export const lootFx = computed(() => _fx.value);
let fxSeq = 0;
export function consumeLootFx(seq: number): void {
  _fx.value = _fx.value.filter((f) => f.seq > seq);
}

/** Phần trang bị cộng vào tu luyện suy từ đồ đang mặc; `settle: false` khi nạp save (không quyết toán). */
function syncGear(eq: Equipped, settle = true): void {
  const c = combatStats(playerBase(cultivation.peek()), eq as Loadout);
  setGearBonus(gearCultBonus(c.utility), { settle });
}

function receiveCtx(inv: InventoryState, autoEquip: boolean) {
  const c = cultivation.peek();
  return {
    base: playerBase(c),
    filter: inv.filter,
    playerGiai: playerGiai(c),
    capacity: BALANCE.BAG_CAPACITY,
    autoEquip,
  };
}

/** Nhận đồ: Tiên+ vào Rương Chưa Mở; còn lại qua tự mặc / bộ lọc / túi. */
export function receive(
  items: readonly ItemInstance[],
  o: { autoEquip: boolean; online: boolean },
): ReceiveReport | null {
  if (items.length === 0) return null;
  const inv = _inv.peek();
  const sealed = items.filter((i) => rarityIndex(i.rarity) >= rarityIndex('immortal'));
  const open = items.filter((i) => rarityIndex(i.rarity) < rarityIndex('immortal'));
  const r = receiveItems({ equipped: inv.equipped, bag: inv.bag }, open, receiveCtx(inv, o.autoEquip));
  batch(() => {
    _inv.value = {
      ...inv,
      equipped: r.gear.equipped,
      bag: r.gear.bag,
      unopened: [...inv.unopened, ...sealed],
      fresh: [...inv.fresh, ...r.report.stored.map((i) => i.uid)].slice(-BALANCE.BAG_CAPACITY),
    };
    creditWallet({ dust: r.report.dust, refiningStone: r.report.refiningStone });
    if (r.report.equipped.length) syncGear(r.gear.equipped);
    if (o.online) {
      const seen = [...r.report.stored, ...r.report.equipped.map((e) => e.item)];
      _fx.value = [..._fx.value, ...seen.map((i) => ({ seq: ++fxSeq, rarity: i.rarity, uid: i.uid }))].slice(
        -12,
      );
    }
  });
  return r.report;
}

export function addGoods(delta: Readonly<Record<string, number>>): void {
  const g = { ..._inv.peek().goods };
  for (const [k, n] of Object.entries(delta)) if (isStackKey(k) && n > 0) g[k] = (g[k] ?? 0) + n;
  _inv.value = { ..._inv.peek(), goods: g };
}

export function goodsCount(key: string): number {
  return _inv.peek().goods[key] ?? 0;
}

/** Trừ vật phẩm xếp chồng; thiếu thì không trừ gì. */
export function takeGoods(cost: Readonly<Record<string, number>>): boolean {
  const g = { ..._inv.peek().goods };
  for (const [k, n] of Object.entries(cost)) {
    if ((g[k] ?? 0) < n) return false;
    g[k] = (g[k] ?? 0) - n;
    if (g[k] === 0) delete g[k];
  }
  _inv.value = { ..._inv.peek(), goods: g };
  return true;
}

// ── Hành động
function findBag(uid: string): ItemInstance | undefined {
  return _inv.peek().bag.find((i) => i.uid === uid);
}

export function findItem(uid: string): { item: ItemInstance; slot: EquipSlot | null } | null {
  const inv = _inv.peek();
  for (const s of EQUIP_SLOTS) if (inv.equipped[s]?.uid === uid) return { item: inv.equipped[s]!, slot: s };
  const b = findBag(uid);
  return b ? { item: b, slot: null } : null;
}

/** Khóa i18n tên món cho Truyền Âm (độc bản đọc tên riêng). */
function itemNameKey(it: ItemInstance): string {
  return `@${it.uniqueId ?? it.base}.name`;
}

export interface EquipResult {
  slot: EquipSlot;
  /** Món vừa bị thay (đã về túi), để UI mời chuyển cấp cường hóa. */
  replaced: ItemInstance | null;
}

export function equip(uid: string, slot?: EquipSlot): EquipResult | null {
  const inv = _inv.peek();
  const it = findBag(uid);
  if (!it) return null;
  const c = cultivation.peek();
  const target =
    slot && slotsForKind(baseDef(it.base).slot).includes(slot)
      ? slot
      : compareToEquipped(playerBase(c), inv.equipped as Loadout, it).slot;
  const old = inv.equipped[target];
  const eq = { ...inv.equipped, [target]: it };
  const bag = inv.bag.filter((x) => x.uid !== uid);
  // Đổi chỗ một-một: túi không phình (món cũ vào đúng chỗ món mới vừa ra).
  if (old) bag.push(old);
  batch(() => {
    _inv.value = { ...inv, equipped: eq, bag, fresh: inv.fresh.filter((x) => x !== uid) };
    syncGear(eq);
    logLine('system', 'log.item.equip', { item: itemNameKey(it) });
  });
  markDirty();
  return { slot: target, replaced: old };
}

/** Báo giá chuyển cấp từ món `fromUid` (trong túi) sang món `toUid` đang mặc cùng loại ô. */
export function enhanceTransferQuote(fromUid: string, toUid: string): EnhanceTransfer | null {
  const from = findItem(fromUid);
  const to = findItem(toUid);
  if (!from || !to || from.slot !== null || to.slot === null) return null;
  return transferQuote(from.item, to.item, baseDef(from.item.base).slot === baseDef(to.item.base).slot);
}

/** Chuyển cấp cường hóa (ITEMS §8.1): món mới giữ 80% cấp món cũ (làm tròn xuống), món cũ về +0. */
export function transferEnhance(fromUid: string, toUid: string): boolean {
  const q = enhanceTransferQuote(fromUid, toUid);
  if (!q || !canAfford({ spiritStone: q.spiritStone })) return false;
  const inv = _inv.peek();
  const to = findItem(toUid)!;
  const from = findItem(fromUid)!;
  const r = applyTransfer(from.item, to.item, q);
  batch(() => {
    spend({ spiritStone: q.spiritStone });
    const eq = { ...inv.equipped, [to.slot!]: r.to };
    _inv.value = { ...inv, equipped: eq, bag: inv.bag.map((i) => (i.uid === fromUid ? r.from : i)) };
    syncGear(eq);
    logLine('system', 'log.item.transfer', { item: itemNameKey(r.to), n: q.level });
  });
  void flush();
  return true;
}

export type UnequipBlock = 'bagFull';
/** Gỡ đồ cần một ô trống trong túi (không cho túi vượt trần). */
export function unequipBlock(): UnequipBlock | null {
  return _inv.peek().bag.length >= BALANCE.BAG_CAPACITY ? 'bagFull' : null;
}

export function unequip(slot: EquipSlot): boolean {
  const inv = _inv.peek();
  const it = inv.equipped[slot];
  if (!it || unequipBlock() !== null) return false;
  const eq = { ...inv.equipped, [slot]: null };
  batch(() => {
    _inv.value = { ...inv, equipped: eq, bag: [...inv.bag, it] };
    syncGear(eq);
  });
  markDirty();
  return true;
}

/**
 * Phân giải tay cần hỏi lại (dải hỏi, UI_SPEC §4.3): Thiên phẩm trở lên, hoặc món được bảo vệ khỏi
 * tự phân giải (độc bản, Đạo Văn, dòng Cực, đã cường hóa). Món khóa thì không phân giải được.
 */
export function salvageNeedsConfirm(it: ItemInstance): boolean {
  return rarityIndex(it.rarity) >= rarityIndex('heaven') || isProtected(it);
}

export function salvage(uid: string): boolean {
  const inv = _inv.peek();
  const it = findBag(uid);
  if (!it || it.locked) return false;
  const y = salvageYield(it);
  batch(() => {
    _inv.value = {
      ...inv,
      bag: inv.bag.filter((x) => x.uid !== uid),
      fresh: inv.fresh.filter((x) => x !== uid),
    };
    creditWallet({ dust: y.dust, refiningStone: y.refiningStone });
    logLine('system', 'log.item.salvage', { n: 1, dust: y.dust });
  });
  void flush();
  return true;
}

/** Áp bộ lọc lên túi ngay (nút "Phân giải theo bộ lọc"). */
export function salvageByFilter(): number {
  const inv = _inv.peek();
  const g = playerGiai(cultivation.peek());
  const f = { ...inv.filter, enabled: true };
  const out = inv.bag.filter((i) => matchesFilter(i, f, g));
  if (!out.length) return 0;
  let dust = 0;
  let rs = 0;
  for (const i of out) {
    const y = salvageYield(i);
    dust += y.dust;
    rs += y.refiningStone;
  }
  const gone = new Set(out.map((i) => i.uid));
  batch(() => {
    _inv.value = {
      ...inv,
      bag: inv.bag.filter((i) => !gone.has(i.uid)),
      fresh: inv.fresh.filter((u) => !gone.has(u)),
    };
    creditWallet({ dust, refiningStone: rs });
    logLine('system', 'log.item.salvage', { n: out.length, dust });
  });
  void flush();
  return out.length;
}

/** Phẩm cao nhất bộ lọc tự phân giải chọn được (Thiên+ luôn qua dải hỏi, không tự phân giải). */
export const FILTER_MAX_RARITY: Rarity = 'earth';

export function setFilter(f: SalvageFilter): void {
  const maxRarity =
    rarityIndex(f.maxRarity) > rarityIndex(FILTER_MAX_RARITY) ? FILTER_MAX_RARITY : f.maxRarity;
  _inv.value = { ..._inv.peek(), filter: { ...f, maxRarity } };
  markDirty();
}

export function toggleLock(uid: string): void {
  const inv = _inv.peek();
  const flip = (i: ItemInstance): ItemInstance => (i.uid === uid ? { ...i, locked: !i.locked } : i);
  const eq = Object.fromEntries(
    EQUIP_SLOTS.map((s) => [s, inv.equipped[s] ? flip(inv.equipped[s]!) : null]),
  ) as Equipped;
  _inv.value = { ...inv, bag: inv.bag.map(flip), equipped: eq };
  markDirty();
}

export function markSeen(uid: string): void {
  const inv = _inv.peek();
  if (inv.fresh.includes(uid)) _inv.value = { ...inv, fresh: inv.fresh.filter((x) => x !== uid) };
}

export type EnhanceOutcome = 'success' | 'fail' | 'blocked';

export function enhance(uid: string): EnhanceOutcome {
  const found = findItem(uid);
  if (!found) return 'blocked';
  const cost = enhanceCost(found.item);
  if (!cost || !canAfford(cost)) return 'blocked';
  const rng = createRng(
    deriveSeed(saveSeed(), 'enhance', uid, found.item.enhance, found.item.enhanceFailStacks),
  );
  const r = enhanceOnce(found.item, rng);
  const inv = _inv.peek();
  batch(() => {
    spend(cost);
    const eq = found.slot ? { ...inv.equipped, [found.slot]: r.item } : inv.equipped;
    _inv.value = { ...inv, equipped: eq, bag: inv.bag.map((i) => (i.uid === uid ? r.item : i)) };
    if (found.slot) syncGear(eq);
    logLine('system', r.success ? 'log.item.enhanceOk' : 'log.item.enhanceFail', {
      item: itemNameKey(r.item),
      n: r.item.enhance,
    });
  });
  void flush();
  return r.success ? 'success' : 'fail';
}

/**
 * Mở một món trong Rương Chưa Mở (sau nghi thức Khai Bảo): vào túi, không tự mặc. Qua cùng ống nhận
 * đồ: túi đầy và món không được bảo vệ thì phân giải (Bụi / Luyện Khí Thạch vào ví, Truyền Âm ghi phân
 * giải thay vì Khai Bảo).
 */
export function openSealed(uid: string): ItemInstance | null {
  const inv = _inv.peek();
  const it = inv.unopened.find((i) => i.uid === uid);
  if (!it) return null;
  const r = receiveItems({ equipped: inv.equipped, bag: inv.bag }, [it], receiveCtx(inv, false));
  const stored = r.report.stored.some((x) => x.uid === it.uid);
  batch(() => {
    _inv.value = {
      ...inv,
      unopened: inv.unopened.filter((i) => i.uid !== uid),
      bag: r.gear.bag,
      fresh: stored ? [...inv.fresh, it.uid].slice(-BALANCE.BAG_CAPACITY) : inv.fresh,
    };
    creditWallet({ dust: r.report.dust, refiningStone: r.report.refiningStone });
    if (stored) logLine('system', 'log.item.revealed', { item: itemNameKey(it) });
    else logLine('system', 'log.item.salvage', { n: r.report.salvagedCount, dust: r.report.dust });
  });
  void flush();
  return it;
}

/** Uống đan từ túi (Tụ Khí Đan…): hiệu lực theo đan độc lúc uống (đã luyện hóa tới hiện tại). */
export function consumePill(key: string): boolean {
  const n = now();
  // Quyết toán trước: đan độc đã luyện hóa tới lúc uống, không để applyPillUse ghi đè bằng số cũ.
  settleTo(n, true);
  const c = cultivation.peek();
  if (pillBlock(key, c.pillToxicity) !== null || goodsCount(key) < 1) return false;
  const res = combatStats(playerBase(c), _inv.peek().equipped as Loadout).utility.pillToxRes;
  const use = simUsePill(key, c.pillToxicity, n, res);
  batch(() => {
    takeGoods({ [key]: 1 });
    applyPillUse(use, n);
    logLine('cultivation', 'log.pill.use', {
      item: `@${parseStackKey(key).id}.name`,
      pct: Math.round(use.efficacy * 100),
    });
  });
  void flush();
  return true;
}

// ── Nối vào vòng idle của tu luyện: ngữ cảnh Lịch Luyện + nơi đổ loot.
setIdleHooks({
  trialCtx: (c, eff) => trialCtxFor(c, _inv.peek().equipped, eff),
  // Trả bản kê nhận đồ cho vòng idle (Hồi Sơn: tự mặc / phân giải / phân giải vì túi đầy).
  loot: (loot, online) =>
    batch(() => {
      creditWallet(loot.currency);
      addGoods(loot.goods);
      return receive(loot.items, { autoEquip: true, online });
    }),
});

// ── Save (core.inventory, từ v4). Làm sạch từng món; món hỏng bị bỏ, không làm hỏng cả túi.
function num(v: unknown, d: number, min = -Infinity, max = Infinity): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : d;
}

/**
 * Giai hợp lệ = realm × 10 + s với s trong 1..số tiểu cảnh của realm (đọc từ data, không hard-code).
 * Sai (20, 25–29, 95…) thì kẹp về tiểu cảnh gần nhất của cùng cảnh giới; không phải số thì về 11.
 */
function sanitizeGiai(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return FIRST_REALM * 10 + 1;
  const g = Math.floor(v);
  const realm = Math.max(FIRST_REALM, Math.min(LAST_REALM, Math.floor(g / 10)));
  const s = realm === Math.floor(g / 10) ? g % 10 : g < FIRST_REALM * 10 ? 1 : subStageCount(realm);
  return realm * 10 + Math.max(1, Math.min(subStageCount(realm), s));
}

export function sanitizeItem(d: unknown): ItemInstance | null {
  if (!isPlainObject(d) || typeof d.uid !== 'string' || !isBaseId(d.base) || !isRarity(d.rarity)) return null;
  const lines = Array.isArray(d.lines)
    ? d.lines.flatMap((l) => {
        if (!isPlainObject(l) || typeof l.affix !== 'string' || !maybeAffix(l.affix)) return [];
        const tier = num(l.tier, 5, 1, 5);
        return [
          {
            affix: l.affix,
            tier: Math.round(tier) as 1 | 2 | 3 | 4 | 5,
            notch: Math.floor(num(l.notch, 0, 0, BALANCE.AFFIX_ROLL_NOTCHES - 1)),
            value: num(l.value, 0),
          },
        ];
      })
    : [];
  const it: ItemInstance = {
    uid: d.uid,
    base: d.base,
    rarity: d.rarity,
    giai: sanitizeGiai(d.giai),
    lines,
    enhance: Math.floor(num(d.enhance, 0, 0, BALANCE.ENHANCE_MAX)),
    enhanceFailStacks: Math.floor(num(d.enhanceFailStacks, 0, 0, 100)),
    source: typeof d.source === 'string' ? d.source : 'unknown',
    gotAtMs: num(d.gotAtMs, 0),
  };
  if (isPlainObject(d.daoGlyph) && isGlyphId(d.daoGlyph.id))
    it.daoGlyph = { id: d.daoGlyph.id, level: Math.floor(num(d.daoGlyph.level, 1, 1, 5)) };
  if (isUniqueId(d.uniqueId)) it.uniqueId = d.uniqueId;
  if (d.locked === true) it.locked = true;
  return it;
}

export function sanitizeInventory(d: unknown): InventoryState {
  const def = defaultInventory();
  if (!isPlainObject(d)) return def;
  const eqRaw = isPlainObject(d.equipped) ? d.equipped : {};
  const equipped = emptyEquipped();
  for (const s of EQUIP_SLOTS) {
    const it = sanitizeItem(eqRaw[s]);
    if (it && slotsForKind(baseDef(it.base).slot).includes(s)) equipped[s] = it;
  }
  const items = (v: unknown): ItemInstance[] =>
    Array.isArray(v) ? v.map(sanitizeItem).filter((x): x is ItemInstance => x !== null) : [];
  const goods: Record<string, number> = {};
  if (isPlainObject(d.goods))
    for (const [k, v] of Object.entries(d.goods))
      if (isStackKey(k) && num(v, 0, 1) > 0) goods[k] = Math.floor(v as number);
  const f = isPlainObject(d.filter) ? d.filter : {};
  return {
    equipped,
    bag: items(d.bag),
    goods,
    filter: {
      enabled: f.enabled === true,
      // Bộ lọc chỉ chọn tới Địa phẩm (FilterWindow); save lệch thì kẹp về Địa.
      maxRarity: isRarity(f.maxRarity)
        ? rarityIndex(f.maxRarity) > rarityIndex(FILTER_MAX_RARITY)
          ? FILTER_MAX_RARITY
          : f.maxRarity
        : def.filter.maxRarity,
      requireNoHighTier: f.requireNoHighTier === true,
      giaiGap: f.giaiGap === null || f.giaiGap === undefined ? null : Math.floor(num(f.giaiGap, 0, 0, 90)),
    },
    unopened: items(d.unopened),
    fresh: Array.isArray(d.fresh) ? d.fresh.filter((x): x is string => typeof x === 'string') : [],
  };
}

registerSlice<InventoryState>({
  key: 'inventory',
  scope: 'core',
  serialize: () => _inv.peek(),
  hydrate: (d) => {
    _inv.value = sanitizeInventory(d);
    // Chỉ gán phần trang bị (suy từ đồ đang mặc), không quyết toán: quãng vắng để resumeFromAbsence.
    syncGear(_inv.peek().equipped, false);
  },
});

/** Hook dev / perf: nhận ngay các món (bỏ qua Lịch Luyện). */
export function devGrantItems(items: ItemInstance[]): void {
  receive(items, { autoEquip: false, online: true });
}

export { goodsDef, isProtected };
